import { useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Header } from '@/components/Header';
import { Footer } from '@/components/Footer';
import { SEOHead } from '@/components/SEOHead';
import { ServiceLeadForm } from '@/components/ServiceLeadForm';
import { FiscalBanner } from '@/components/FiscalBanner';
import { FiscalCountdown } from '@/components/FiscalCountdown';
import { FiscalSimulator } from '@/components/FiscalSimulator';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { 
  ChefHat, Bath, Home, ArrowRight, Check, X, Star, 
  Calculator, MapPin, Mail
} from 'lucide-react';

// Sub-service configurations
const subServiceConfig = {
  cuisine: {
    name: 'Cuisine',
    icon: ChefHat,
    heroImage: 'https://images.unsplash.com/photo-1556909114-f6e7ad7d3136?w=1600&auto=format&fit=crop',
    titleTemplate: 'Rénovation cuisine à {city} | Déductions fiscales avant 2028',
    h1Template: 'Rénovation de cuisine à {city}',
    subtitleTemplate: 'Profitez des déductions fiscales actuelles pour rénover votre cuisine à {city}. Après la réforme, ces avantages disparaîtront.',
    descriptionTemplate: 'Rénovation de cuisine à {city} avec optimisation fiscale. Cuisine sur mesure, IKEA ou haut de gamme. Économisez jusqu\'à 25% grâce aux déductions actuelles.',
    examples: [
      { type: 'Cuisine IKEA', budget: 15000 },
      { type: 'Cuisine sur mesure', budget: 35000 },
      { type: 'Cuisine haut de gamme', budget: 60000 }
    ],
    questions: [
      { id: 'type_cuisine', label: 'Type de projet', type: 'select', options: ['Rénovation complète', 'Remplacement meubles', 'Plan de travail', 'Électroménager'] },
      { id: 'surface', label: 'Surface cuisine', type: 'text', placeholder: 'Ex: 12 m²' },
      { id: 'budget', label: 'Budget estimatif', type: 'text', placeholder: 'Ex: 35\'000 CHF' }
    ],
    benefits: [
      'Conception personnalisée',
      'Pose par artisans qualifiés',
      'Plomberie et électricité',
      'Accompagnement fiscal'
    ]
  },
  'salle-de-bain': {
    name: 'Salle de bain',
    icon: Bath,
    heroImage: 'https://images.unsplash.com/photo-1552321554-5fefe8c9ef14?w=1600&auto=format&fit=crop',
    titleTemplate: 'Rénovation salle de bain à {city} | Déductions fiscales avant 2028',
    h1Template: 'Rénovation salle de bain à {city}',
    subtitleTemplate: 'Transformez votre salle de bain à {city} tout en bénéficiant des déductions fiscales. Fenêtre d\'opportunité limitée.',
    descriptionTemplate: 'Rénovation de salle de bain à {city} avec optimisation fiscale. Douche italienne, baignoire, double vasque. Économisez jusqu\'à 25% sur vos travaux.',
    examples: [
      { type: 'Rafraîchissement', budget: 12000 },
      { type: 'Rénovation standard', budget: 25000 },
      { type: 'Salle de bain luxe', budget: 45000 }
    ],
    questions: [
      { id: 'type_sdb', label: 'Type de projet', type: 'select', options: ['Rénovation complète', 'Douche italienne', 'Baignoire', 'Double vasque'] },
      { id: 'surface', label: 'Surface', type: 'text', placeholder: 'Ex: 8 m²' },
      { id: 'budget', label: 'Budget estimatif', type: 'text', placeholder: 'Ex: 25\'000 CHF' }
    ],
    benefits: [
      'Conception 3D',
      'Carrelage et faïence',
      'Plomberie complète',
      'Accompagnement fiscal'
    ]
  },
  complete: {
    name: 'Rénovation complète',
    icon: Home,
    heroImage: 'https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?w=1600&auto=format&fit=crop',
    titleTemplate: 'Rénovation complète à {city} | Économisez des milliers de CHF avant 2028',
    h1Template: 'Rénovation complète à {city}',
    subtitleTemplate: 'Transformez votre appartement ou maison à {city}. Plus le projet est grand, plus l\'économie fiscale est importante.',
    descriptionTemplate: 'Rénovation complète d\'appartement et maison à {city} avec optimisation fiscale. Cuisine, salle de bain, sols, peinture. Économie pouvant dépasser 20\'000 CHF.',
    examples: [
      { type: 'Appartement 3 pièces', budget: 60000 },
      { type: 'Appartement 5 pièces', budget: 100000 },
      { type: 'Maison individuelle', budget: 180000 }
    ],
    questions: [
      { id: 'type_bien', label: 'Type de bien', type: 'select', options: ['Appartement', 'Maison', 'Chalet', 'Duplex'] },
      { id: 'surface', label: 'Surface totale', type: 'text', placeholder: 'Ex: 120 m²' },
      { id: 'travaux', label: 'Travaux envisagés', type: 'select', options: ['Tout refaire', 'Pièces principales', 'Sols + peinture'] },
      { id: 'budget', label: 'Budget estimatif', type: 'text', placeholder: 'Ex: 80\'000 CHF' }
    ],
    benefits: [
      'Coordination tous corps de métier',
      'Suivi de chantier',
      'Garantie décennale',
      'Optimisation fiscale maximale'
    ]
  }
};

// Format city name
const formatCity = (city) => {
  if (!city) return '';
  return city
    .split('-')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join('-');
};

export const RenovationSubServiceCityPage = ({ subService }) => {
  const { city } = useParams();
  const formRef = useRef(null);
  const simulatorRef = useRef(null);
  
  const config = subServiceConfig[subService];
  const formattedCity = formatCity(city);
  const IconComponent = config?.icon || Home;
  
  if (!config || !city) {
    return (
      <div className="min-h-screen">
        <Header />
        <div className="flex flex-col items-center justify-center py-32">
          <h1 className="text-4xl font-bold mb-4">Page non trouvée</h1>
          <Link to="/renovation">
            <Button>Retour à Rénovation</Button>
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  const scrollToForm = () => {
    formRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const scrollToSimulator = () => {
    simulatorRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  // Replace {city} in templates
  const title = config.titleTemplate.replace('{city}', formattedCity);
  const h1 = config.h1Template.replace('{city}', formattedCity);
  const subtitle = config.subtitleTemplate.replace('{city}', formattedCity);
  const description = config.descriptionTemplate.replace('{city}', formattedCity);

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": `${config.name} à ${formattedCity}`,
    "provider": {
      "@type": "LocalBusiness",
      "name": "Homely Solutions",
      "address": {
        "@type": "PostalAddress",
        "streetAddress": "Rue Jean-André Venel 4",
        "addressLocality": "Yverdon-les-Bains",
        "postalCode": "1400",
        "addressCountry": "CH"
      }
    },
    "areaServed": formattedCity,
    "description": description
  };

  return (
    <div className="min-h-screen bg-background" data-testid={`renovation-${subService}-${city}-page`}>
      <SEOHead 
        title={title}
        description={description}
        canonical={`https://www.homely-solutions.ch/renovation/${subService}/${city}`}
        jsonLd={jsonLd}
      />

      {/* Bannière fiscale urgente */}
      <FiscalBanner onSimulatorClick={scrollToSimulator} />
      
      <Header />
      
      {/* Hero Section */}
      <section className="hero-section relative min-h-[75vh] flex items-center">
        <img 
          src={config.heroImage} 
          alt={h1} 
          className="hero-image-bg"
        />
        <div className="mx-auto w-full max-w-[var(--container)] px-4 sm:px-6 lg:px-8 py-20">
          <div className="max-w-3xl">
            <div className="flex items-center gap-3 mb-6">
              <IconComponent className="h-10 w-10 text-white" />
              <Badge className="bg-orange-500 text-white border-orange-400">
                <MapPin className="h-4 w-4 mr-1" />
                {formattedCity}
              </Badge>
            </div>
            
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6 text-white leading-tight" style={{ fontFamily: 'var(--font-heading)' }}>
              {h1}
              <span className="block text-orange-300 mt-2 text-3xl sm:text-4xl">Profitez des déductions fiscales avant qu'il ne soit trop tard.</span>
            </h1>
            
            <p className="text-lg sm:text-xl text-white/90 mb-8 max-w-2xl">
              {subtitle}
            </p>

            {/* Countdown */}
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 mb-8 inline-block">
              <FiscalCountdown />
            </div>
            
            {/* CTAs */}
            <div className="flex flex-col sm:flex-row gap-4">
              <Button 
                size="lg" 
                className="h-14 px-10 text-base rounded-full bg-orange-500 hover:bg-orange-600"
                onClick={scrollToSimulator}
              >
                <Calculator className="mr-2 h-5 w-5" />
                Simuler mon économie
              </Button>
              <Button 
                size="lg" 
                variant="outline"
                className="h-14 px-8 text-base rounded-full bg-white/10 border-white/30 text-white hover:bg-white hover:text-[var(--anthracite)]"
                onClick={scrollToForm}
              >
                Demander un devis à {formattedCity}
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Comparison Section */}
      <section className="py-16 bg-gradient-to-b from-gray-50 to-white">
        <div className="mx-auto w-full max-w-[var(--container)] px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <Badge className="mb-4 bg-red-100 text-red-700">Urgence fiscale à {formattedCity}</Badge>
            <h2 className="text-3xl font-bold mb-4" style={{ fontFamily: 'var(--font-heading)' }}>
              Aujourd'hui vs Après la réforme
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            <div className="bg-green-50 border-2 border-green-200 rounded-2xl p-8">
              <h3 className="text-xl font-bold text-green-800 mb-6 flex items-center gap-2">
                <div className="w-4 h-4 bg-green-500 rounded-full"></div>
                Rénover maintenant
              </h3>
              <ul className="space-y-4">
                {[
                  'Déduction fiscale sur les travaux',
                  'Coût réel réduit de ~25%',
                  `${config.name} + économie d'impôts`
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <Check className="h-6 w-6 text-green-600 flex-shrink-0" />
                    <span className="text-green-900">{item}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="bg-red-50 border-2 border-red-200 rounded-2xl p-8">
              <h3 className="text-xl font-bold text-red-800 mb-6 flex items-center gap-2">
                <div className="w-4 h-4 bg-red-500 rounded-full"></div>
                Attendre la réforme
              </h3>
              <ul className="space-y-4">
                {[
                  'Plus aucune déduction possible',
                  'Même travaux, prix plein',
                  'Opportunité perdue définitivement'
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <X className="h-6 w-6 text-red-600 flex-shrink-0" />
                    <span className="text-red-900">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Simulator Section */}
      <section className="py-16 bg-gray-50" ref={simulatorRef}>
        <div className="mx-auto w-full max-w-[var(--container)] px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
            <div>
              <Badge className="mb-4 bg-orange-100 text-orange-700">Simulation {config.name.toLowerCase()}</Badge>
              <h2 className="text-3xl font-bold mb-6" style={{ fontFamily: 'var(--font-heading)' }}>
                Combien coûte réellement votre {config.name.toLowerCase()} à {formattedCity} ?
              </h2>
              <p className="text-lg text-muted-foreground mb-8">
                Calculez immédiatement votre économie fiscale potentielle.
              </p>
              <FiscalCountdown />
            </div>
            <FiscalSimulator onCtaClick={scrollToForm} />
          </div>
        </div>
      </section>

      {/* Examples Section */}
      <section className="py-16">
        <div className="mx-auto w-full max-w-[var(--container)] px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold mb-4" style={{ fontFamily: 'var(--font-heading)' }}>
              Exemples de projets à {formattedCity}
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {config.examples.map((example, idx) => {
              const economie = Math.round(example.budget * 0.25);
              const coutReel = example.budget - economie;
              
              return (
                <div key={idx} className="bg-white rounded-2xl shadow-lg overflow-hidden border">
                  <div className="p-6">
                    <h3 className="text-xl font-bold mb-4">{example.type}</h3>
                    
                    <div className="bg-gray-50 rounded-xl p-4 space-y-3">
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Budget</span>
                        <span className="font-semibold">{example.budget.toLocaleString('fr-CH')} CHF</span>
                      </div>
                      
                      <div className="border-t pt-3">
                        <div className="flex justify-between text-green-600">
                          <span>Économie fiscale</span>
                          <span className="font-semibold">-{economie.toLocaleString('fr-CH')} CHF</span>
                        </div>
                        <div className="flex justify-between text-green-700 font-bold mt-1">
                          <span>Coût réel aujourd'hui</span>
                          <span>{coutReel.toLocaleString('fr-CH')} CHF</span>
                        </div>
                      </div>
                      
                      <div className="border-t pt-3">
                        <div className="flex justify-between text-red-600">
                          <span>Après réforme</span>
                          <span className="font-bold">{example.budget.toLocaleString('fr-CH')} CHF</span>
                        </div>
                      </div>
                    </div>
                    
                    <div className="mt-4 p-3 bg-orange-100 rounded-lg text-center">
                      <span className="text-orange-800 font-bold">
                        Perte si attente : +{economie.toLocaleString('fr-CH')} CHF
                      </span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-16 gradient-premium">
        <div className="mx-auto w-full max-w-[var(--container)] px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <Badge className="mb-4" style={{ backgroundColor: 'var(--sage)', color: 'white' }}>
                Nos prestations à {formattedCity}
              </Badge>
              <h2 className="text-3xl font-bold mb-6" style={{ fontFamily: 'var(--font-heading)' }}>
                {config.name} clé en main
              </h2>
              
              <ul className="space-y-4 mb-8">
                {config.benefits.map((item, i) => (
                  <li key={i} className="flex items-center gap-3">
                    <div className="w-6 h-6 rounded-full bg-[var(--sage)] flex items-center justify-center">
                      <Check className="h-4 w-4 text-white" />
                    </div>
                    <span className="text-lg">{item}</span>
                  </li>
                ))}
              </ul>

              <Button 
                size="lg" 
                className="h-14 px-10 rounded-full bg-orange-500 hover:bg-orange-600"
                onClick={scrollToForm}
              >
                Demander un devis gratuit
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </div>

            <div className="bg-white rounded-2xl p-6 shadow-xl">
              <div className="text-center mb-6">
                <IconComponent className="h-12 w-12 text-[var(--sage)] mx-auto mb-4" />
                <h3 className="text-xl font-bold">{config.name} à {formattedCity}</h3>
              </div>
              <div className="space-y-3">
                {[
                  { label: 'Devis gratuit', value: 'Sous 48h' },
                  { label: 'Visite sur place', value: 'Gratuite' },
                  { label: 'Zone couverte', value: formattedCity + ' et environs' },
                  { label: 'Conseil fiscal', value: 'Inclus' }
                ].map((item, idx) => (
                  <div key={idx} className="flex justify-between py-2 border-b last:border-0">
                    <span className="text-muted-foreground">{item.label}</span>
                    <span className="font-medium">{item.value}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonial */}
      <section className="py-16">
        <div className="mx-auto w-full max-w-[var(--container)] px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl mx-auto text-center">
            <div className="flex justify-center gap-1 mb-4">
              {[...Array(5)].map((_, i) => (
                <Star key={i} className="h-6 w-6 fill-yellow-400 text-yellow-400" />
              ))}
            </div>
            <p className="text-xl italic text-muted-foreground mb-6">
              "Grâce à Homely Solutions, nous avons pu rénover notre {config.name.toLowerCase()} 
              tout en économisant sur nos impôts. L'équipe nous a accompagnés du début à la fin."
            </p>
            <p className="font-semibold">— Client satisfait, {formattedCity}</p>
          </div>
        </div>
      </section>

      {/* Other sub-services links */}
      <section className="py-12 bg-gray-50">
        <div className="mx-auto w-full max-w-[var(--container)] px-4 sm:px-6 lg:px-8">
          <p className="text-center text-muted-foreground mb-6">Autres projets de rénovation à {formattedCity} :</p>
          <div className="flex flex-wrap justify-center gap-4">
            {subService !== 'cuisine' && (
              <Link to={`/renovation/cuisine/${city}`} className="px-6 py-3 bg-white border rounded-full hover:bg-[var(--sage)] hover:text-white transition-colors">
                Cuisine
              </Link>
            )}
            {subService !== 'salle-de-bain' && (
              <Link to={`/renovation/salle-de-bain/${city}`} className="px-6 py-3 bg-white border rounded-full hover:bg-[var(--sage)] hover:text-white transition-colors">
                Salle de bain
              </Link>
            )}
            {subService !== 'complete' && (
              <Link to={`/renovation/complete/${city}`} className="px-6 py-3 bg-white border rounded-full hover:bg-[var(--sage)] hover:text-white transition-colors">
                Rénovation complète
              </Link>
            )}
            <Link to={`/renovation/${city}`} className="px-6 py-3 bg-white border rounded-full hover:bg-[var(--sage)] hover:text-white transition-colors">
              Tous nos services
            </Link>
          </div>
        </div>
      </section>

      {/* Form Section */}
      <section className="py-20" style={{ backgroundColor: 'var(--anthracite)' }} ref={formRef}>
        <div className="mx-auto w-full max-w-[var(--container)] px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            <div className="text-white">
              <Badge className="mb-4 bg-orange-500">Devis gratuit à {formattedCity}</Badge>
              <h2 className="text-3xl sm:text-4xl font-bold mb-6" style={{ fontFamily: 'var(--font-heading)' }}>
                Votre projet {config.name.toLowerCase()},<br />optimisé fiscalement.
              </h2>
              <p className="text-white/70 mb-8 text-lg">
                Décrivez votre projet à {formattedCity} et recevez une estimation 
                incluant le calcul de votre économie fiscale potentielle.
              </p>

              <div className="space-y-4 mb-8">
                {[
                  'Estimation économie fiscale incluse',
                  'Devis détaillé sous 48h',
                  'Visite gratuite sur place',
                  'Sans engagement'
                ].map((item, idx) => (
                  <div key={idx} className="flex items-center gap-3">
                    <div className="w-6 h-6 rounded-full bg-orange-500 flex items-center justify-center">
                      <Check className="h-4 w-4 text-white" />
                    </div>
                    <span className="text-white/90">{item}</span>
                  </div>
                ))}
              </div>

              <div className="mb-8">
                <FiscalCountdown />
              </div>

              {/* Contact Card */}
              <div className="p-4 bg-white/10 rounded-lg">
                <p className="text-white/90 flex items-center gap-2">
                  <Mail className="h-5 w-5" />
                  <a href="mailto:contact@homely-solutions.ch" className="hover:underline">contact@homely-solutions.ch</a>
                </p>
              </div>
            </div>

            <div className="bg-white rounded-2xl p-8 shadow-2xl">
              <div className="mb-6 p-4 bg-orange-50 rounded-lg border border-orange-200">
                <p className="text-sm text-orange-800 font-medium flex items-center gap-2">
                  <IconComponent className="h-5 w-5" />
                  {config.name} à {formattedCity} - Optimisation fiscale
                </p>
              </div>
              <ServiceLeadForm 
                service={`renovation-${subService}`} 
                pole="renovation" 
                questions={[
                  ...config.questions,
                  { id: 'ville', label: 'Ville', type: 'text', placeholder: formattedCity, defaultValue: formattedCity }
                ]}
              />
            </div>
          </div>
        </div>
      </section>

      {/* Footer fiscal */}
      <section className="py-6 bg-gray-100 border-t">
        <div className="mx-auto w-full max-w-[var(--container)] px-4 sm:px-6 lg:px-8">
          <p className="text-sm text-center text-muted-foreground">
            Les règles fiscales évoluent suite à la réforme votée en Suisse en 2025.
            Les déductions actuelles restent applicables pendant la phase transitoire.
            Les estimations présentées sont indicatives (taux ~25%) et varient selon votre situation fiscale.
          </p>
        </div>
      </section>

      <Footer />
    </div>
  );
};
